import { FC } from "react";
import { Box, Chip, useTheme } from "@mui/material";
import type { Theme } from "@mui/material";

import type { TicketDisplayStatus } from "./ticketDisplay";
import { matchTicketDisplayStatus } from "./ticketDisplay";

const getStatusConfig = (
  theme: Theme,
): Record<
  TicketDisplayStatus,
  { bgColor: string; textColor: string; dot?: boolean }
> => ({
  "Awaiting your reply": {
    bgColor: theme.palette.custom.statusOrangeLight,
    textColor: theme.palette.custom.statusOrangeMain,
  },
  "In progress": {
    bgColor: theme.palette.custom.statusBlueLight,
    textColor: theme.palette.custom.statusBlueMain,
  },
  Resolved: {
    bgColor: "transparent",
    textColor: theme.palette.alert_success.main,
    dot: true,
  },
  Closed: {
    bgColor: "transparent",
    textColor: theme.palette.custom.statusGray,
  },
});

interface TicketStatusChipProps {
  /** Raw ticket.state; matched case-insensitively against the known statuses. */
  status: string;
}

const TicketStatusChip: FC<TicketStatusChipProps> = ({ status }) => {
  const theme = useTheme();
  const matched = matchTicketDisplayStatus(status);
  // Unknown states still render as-is, styled like Closed.
  const config = getStatusConfig(theme)[matched ?? "Closed"];

  const label = config.dot ? (
    <Box
      component="span"
      sx={{ display: "inline-flex", alignItems: "center", gap: "5px" }}
    >
      <Box
        component="span"
        sx={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          backgroundColor: config.textColor,
          flexShrink: 0,
        }}
      />
      {matched ?? status}
    </Box>
  ) : (
    matched ?? status
  );

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor: config.bgColor,
        height: "22px",
        borderRadius: "4px",
        width: "fit-content",
        "& .MuiChip-label": {
          color: config.textColor,
          fontWeight: 500,
          fontSize: "0.75rem",
          px: config.bgColor === "transparent" ? 0 : 1,
        },
      }}
    />
  );
};

export default TicketStatusChip;
